<template>
  <div class="profile-page">
    <!-- 个人信息卡片 -->
    <section class="profile-card">
      <!-- Loading 遮罩 -->
      <div v-if="loading" class="loading-overlay profile-loading">
        <div class="loading-spinner">
          <div class="spinner"></div>
          <p class="loading-text">加载中...</p>
        </div>
      </div>

      <div v-show="!loading" class="profile-content">
        <div class="avatar-wrapper" @click="triggerAvatarUpload">
          <img :src="userInfo.avatar" alt="头像" class="avatar" />
          <div class="avatar-overlay">
            <i class="el-icon-picture"></i>
            <span class="upload-text">更换头像</span>
          </div>
          <input
            ref="avatarInput"
            type="file"
            accept="image/*"
            style="display: none"
            @change="handleAvatarChange"
          />
        </div>
        <div class="profile-info">
          <div class="profile-header">
            <h2 class="user-name">{{ userInfo.name }}</h2>
            <span class="user-badge">{{ userInfo.role }}</span>
          </div>
          <div class="profile-meta">
            <span class="meta-item">
              <i class="el-icon-office-building"></i>
              {{ userInfo.department }}
            </span>
            <span class="meta-item">
              <i class="el-icon-link"></i>
              已绑定钉钉
            </span>
          </div>
        </div>
        <div class="action-buttons">
          <button class="edit-btn" @click="showEditDialog">编辑资料</button>
          <button class="password-btn" @click="showPasswordDialog">修改密码</button>
        </div>
      </div>
    </section>

    <!-- 编辑资料对话框 -->
    <el-dialog
      title="编辑个人资料"
      :visible.sync="editDialogVisible"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-form :model="editForm" :rules="editRules" ref="editForm" label-width="80px">
        <el-form-item label="姓名" prop="userName">
          <el-input v-model="editForm.userName" placeholder="请输入姓名"></el-input>
        </el-form-item>
        <el-form-item label="性别" prop="sex">
          <el-select v-model="editForm.sex" placeholder="请选择性别" style="width: 100%">
            <el-option label="男" value="0"></el-option>
            <el-option label="女" value="1"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="手机号" prop="phone">
          <el-input v-model="editForm.phone" placeholder="请输入手机号"></el-input>
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="editForm.email" placeholder="请输入邮箱"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="editDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitEditForm" :loading="submitting">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 修改密码对话框 -->
    <el-dialog
      title="修改密码"
      :visible.sync="passwordDialogVisible"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-form :model="passwordForm" :rules="passwordRules" ref="passwordForm" label-width="100px">
        <el-form-item label="当前密码" prop="password">
          <el-input
            v-model="passwordForm.password"
            type="password"
            placeholder="请输入当前密码"
            show-password
          ></el-input>
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword">
          <el-input
            v-model="passwordForm.newPassword"
            type="password"
            placeholder="请输入新密码"
            show-password
          ></el-input>
        </el-form-item>
        <el-form-item label="确认新密码" prop="confirmPassword">
          <el-input
            v-model="passwordForm.confirmPassword"
            type="password"
            placeholder="请再次输入新密码"
            show-password
          ></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="passwordDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitPasswordForm" :loading="submittingPassword">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 打卡详情对话框 -->
    <el-dialog
      title="学习详情"
      :visible.sync="showDetailDialog"
      width="400px"
    >
      <div v-if="selectedDateDetail" class="detail-content">
        <div class="detail-header">
          <i class="el-icon-date"></i>
          <span class="detail-date">{{ selectedDateDetail.date }}</span>
        </div>
        <div class="detail-stats">
          <div class="detail-item">
            <div class="detail-icon blue">
              <i class="el-icon-date"></i>
            </div>
            <div class="detail-info">
              <p class="detail-label">学习时长</p>
              <p class="detail-value">{{ selectedDateDetail.studyDuration }} 分钟</p>
            </div>
          </div>
          <div class="detail-item">
            <div class="detail-icon purple">
              <i class="el-icon-video-play"></i>
            </div>
            <div class="detail-info">
              <p class="detail-label">观看视频</p>
              <p class="detail-value">{{ selectedDateDetail.watchedVideos }} 个</p>
            </div>
          </div>
          <div class="detail-item">
            <div class="detail-icon green">
              <i class="el-icon-circle-check"></i>
            </div>
            <div class="detail-info">
              <p class="detail-label">完成视频</p>
              <p class="detail-value">{{ selectedDateDetail.completedVideos }} 个</p>
            </div>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="showDetailDialog = false">关 闭</el-button>
      </span>
    </el-dialog>

    <!-- 学习统计区域 -->
    <section class="statistics-section">
      <!-- Loading 遮罩 -->
      <div v-if="loading" class="loading-overlay statistics-loading">
        <div class="loading-spinner">
          <div class="spinner"></div>
          <p class="loading-text">加载中...</p>
        </div>
      </div>

      <div v-show="!loading" class="section-content">
        <div class="section-header">
          <div class="section-indicator"></div>
          <h3 class="section-title">学习统计</h3>
        </div>

        <div class="statistics-grid">
          <!-- 累计学习时长 -->
          <div class="stat-card blue">
            <div class="stat-bg"></div>
            <div class="stat-content">
              <div class="stat-icon blue-icon">
                <i class="el-icon-date"></i>
              </div>
              <div class="stat-info">
                <p class="stat-label">累计学习时长</p>
                <div class="stat-value-wrapper">
                  <span class="stat-value">{{ statistics.totalHours }}</span>
                  <span class="stat-unit">小时</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 已观看视频 -->
          <div class="stat-card purple">
            <div class="stat-bg"></div>
            <div class="stat-content">
              <div class="stat-icon purple-icon">
                <i class="el-icon-video-play"></i>
              </div>
              <div class="stat-info">
                <p class="stat-label">已观看视频</p>
                <div class="stat-value-wrapper">
                  <span class="stat-value">{{ statistics.watchedVideos }}</span>
                  <span class="stat-unit">个</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 已完成视频 -->
          <div class="stat-card green">
            <div class="stat-bg"></div>
            <div class="stat-content">
              <div class="stat-icon green-icon">
                <i class="el-icon-circle-check"></i>
              </div>
              <div class="stat-info">
                <p class="stat-label">已完成视频</p>
                <div class="stat-value-wrapper">
                  <span class="stat-value">{{ statistics.completedVideos }}</span>
                  <span class="stat-unit">个</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 连续打卡 -->
          <div class="stat-card highlight">
            <div class="stat-bg-highlight"></div>
            <div class="stat-content">
              <div class="stat-icon highlight-icon">
                <i class="el-icon-star"></i>
              </div>
              <div class="stat-info">
                <p class="stat-label highlight-label">连续打卡</p>
                <div class="stat-value-wrapper">
                  <span class="stat-value highlight-value">{{ statistics.consecutiveDays }}</span>
                  <span class="stat-unit highlight-unit">天</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 打卡日历 -->
    <section class="calendar-section">
      <div class="calendar-header">
        <h3 class="calendar-title">打卡日历</h3>
        <div class="calendar-controls">
          <button class="control-btn" @click="previousMonth">
            <i class="el-icon-arrow-left"></i>
          </button>
          <span class="current-month">{{ currentMonthDisplay }}</span>
          <button class="control-btn" @click="nextMonth">
            <i class="el-icon-arrow-right"></i>
          </button>
        </div>
      </div>

      <div class="calendar-wrapper">
        <!-- Loading 遮罩 -->
        <div v-show="calendarLoading" class="loading-overlay">
          <div class="loading-spinner">
            <div class="spinner"></div>
            <p class="loading-text">加载中...</p>
          </div>
        </div>

        <!-- 星期标题 -->
        <div v-show="!calendarLoading" class="calendar-weekdays">
          <div class="weekday">周一</div>
          <div class="weekday">周二</div>
          <div class="weekday">周三</div>
          <div class="weekday">周四</div>
          <div class="weekday">周五</div>
          <div class="weekday">周六</div>
          <div class="weekday">周日</div>
        </div>

        <!-- 日历网格 -->
        <div v-show="!calendarLoading" class="calendar-grid">
          <div
            v-for="(day, index) in calendarDays"
            :key="index"
            class="calendar-day"
            :class="{
              'is-today': day.isToday,
              'is-checked': day.isChecked,
              'is-empty': !day.date,
              'is-clickable': day.isChecked && day.detail
            }"
            @click="handleDayClick(day)"
          >
            <span v-if="day.date" class="day-number">{{ day.date }}</span>
            <div v-if="day.isChecked" class="check-dot"></div>
          </div>
        </div>
      </div>

      <div class="calendar-legend">
        <div class="legend-item">
          <div class="legend-dot checked"></div>
          <span>已打卡</span>
        </div>
        <div class="legend-item">
          <div class="legend-dot today"></div>
          <span>今日</span>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { getUserInfo, setUserInfo, changePassword, getMonthlyCheckin, getUserStudyStats, getDailyStudyDetail, getUnifiedMonthlyCheckin } from '@/api/api'
import request from '@/utils/request'
import { DEFAULT_AVATAR_PLACEHOLDER } from '@/utils/placeholders'

export default {
  name: 'Profile',
  data() {
    return {
      // 用户信息
      userInfo: {
        name: '',
        role: '',
        department: '',
        avatar: ''
      },
      // 学习统计
      statistics: {
        totalHours: 0,
        watchedVideos: 0,
        completedVideos: 0,
        consecutiveDays: 0
      },
      // 当前年月
      currentYear: new Date().getFullYear(),
      currentMonth: new Date().getMonth() + 1,
      currentMonthDisplay: '',
      // 日历数据
      calendarDays: [],
      // 打卡数据（从后端获取）
      checkinData: {},
      // 加载状态
      loading: false,
      calendarLoading: false,
      // 详情弹窗
      showDetailDialog: false,
      selectedDateDetail: null,
      // 编辑对话框
      editDialogVisible: false,
      submitting: false,
      editForm: {
        userName: '',
        sex: '',
        phone: '',
        email: ''
      },
      editRules: {
        userName: [
          { required: true, message: '请输入姓名', trigger: 'blur' }
        ],
        phone: [
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
        ],
        email: [
          { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' }
        ]
      },
      // 保存完整的用户数据
      currentUser: null,
      // 修改密码对话框
      passwordDialogVisible: false,
      submittingPassword: false,
      passwordForm: {
        password: '',
        newPassword: '',
        confirmPassword: ''
      },
      passwordRules: {
        password: [
          { required: true, message: '请输入当前密码', trigger: 'blur' }
        ],
        newPassword: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请再次输入新密码', trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              if (value !== this.passwordForm.newPassword) {
                callback(new Error('两次输入的密码不一致'))
              } else {
                callback()
              }
            },
            trigger: 'blur'
          }
        ]
      }
    }
  },
  async created() {
    await this.loadUserInfo()
    await this.loadUserStudyStats()
    this.updateCalendar()
  },
  methods: {
    // 加载用户信息
    async loadUserInfo() {
      try {
        this.loading = true
        const res = await getUserInfo()
        // 检查 success 字段或 code === 1000
        if (res.success === true || res.code === 1000) {
          const user = res.data
          this.currentUser = user
          this.userInfo = {
            name: user.userName || user.loginAccount || '未设置',
            role: this.getUserRole(user.userType),
            department: user.deptName || '未设置',
            avatar: user.avatar || DEFAULT_AVATAR_PLACEHOLDER
          }
        } else {
          this.$message.error(res.message || res.msg || '获取用户信息失败')
        }
      } catch (error) {
        console.error('获取用户信息失败:', error)
        this.$message.error('获取用户信息失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },

    // 加载用户累计学习统计数据
    async loadUserStudyStats() {
      try {
        // 检查用户信息是否已加载
        if (!this.currentUser || !this.currentUser.id) {
          return
        }

        const res = await getUserStudyStats({
          userId: this.currentUser.id
        })

        // 检查响应状态
        if (res.code === 1000 || res.success === true) {
          const data = res.data
          // 将分钟转换为小时（保留一位小数）
          this.statistics.totalHours = Math.round((data.totalStudyDuration / 60) * 10) / 10
          this.statistics.watchedVideos = data.totalWatchedVideos
          this.statistics.completedVideos = data.totalCompletedVideos
          this.statistics.consecutiveDays = data.consecutiveDays
        } else {
          console.error('获取学习统计数据失败:', res.message || res.msg)
        }
      } catch (error) {
        console.error('加载学习统计数据失败:', error)
      }
    },

    // 显示编辑对话框
    showEditDialog() {
      if (!this.currentUser) {
        this.$message.warning('用户信息未加载完成')
        return
      }
      this.editForm = {
        userName: this.currentUser.userName || '',
        sex: this.currentUser.sex !== undefined ? String(this.currentUser.sex) : '',
        phone: this.currentUser.tel || '',
        email: this.currentUser.email || ''
      }
      this.editDialogVisible = true
    },

    // 提交编辑表单
    submitEditForm() {
      this.$refs.editForm.validate(async (valid) => {
        if (!valid) {
          return false
        }
        try {
          this.submitting = true
          const res = await setUserInfo(this.editForm)
          // 检查 success 字段或 code === 1000
          if (res.success === true || res.code === 1000) {
            this.$message.success('保存成功')
            this.editDialogVisible = false
            await this.loadUserInfo()
          } else {
            this.$message.error(res.message || res.msg || '保存失败')
          }
        } catch (error) {
          console.error('保存失败:', error)
          this.$message.error('保存失败，请稍后重试')
        } finally {
          this.submitting = false
        }
      })
    },

    // 触发头像上传
    triggerAvatarUpload() {
      this.$refs.avatarInput.click()
    },

    // 处理头像变化
    handleAvatarChange(event) {
      const file = event.target.files[0]
      if (!file) {
        return
      }

      // 验证文件类型
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp']
      if (!validTypes.includes(file.type)) {
        this.$message.error('请选择正确的图片格式（jpeg、jpg、png、gif、bmp）')
        return
      }

      // 验证文件大小（限制为 5MB）
      if (file.size > 5 * 1024 * 1024) {
        this.$message.error('图片大小不能超过 5MB')
        return
      }

      this.uploadAvatar(file)
    },

    // 上传头像
    async uploadAvatar(file) {
      if (!this.currentUser || !this.currentUser.id) {
        this.$message.error('用户信息未加载完成')
        return
      }

      const formData = new FormData()
      formData.append('file', file)

      try {
        const loading = this.$loading({
          lock: true,
          text: '上传中...',
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.7)'
        })

        const res = await request({
          url: `/user/setUserAvatar/${this.currentUser.id}`,
          method: 'post',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        loading.close()

        // 检查 success 字段或 code === 1000
        if (res.success === true || res.code === 1000) {
          this.$message.success('头像上传成功')
          await this.loadUserInfo()
        } else {
          this.$message.error(res.message || res.msg || '头像上传失败')
        }
      } catch (error) {
        console.error('头像上传失败:', error)
        this.$message.error('头像上传失败，请稍后重试')
      } finally {
        // 清空 input 值，以便可以重复选择同一文件
        this.$refs.avatarInput.value = ''
      }
    },

    // 显示修改密码对话框
    showPasswordDialog() {
      if (!this.currentUser) {
        this.$message.warning('用户信息未加载完成')
        return
      }
      this.passwordForm = {
        password: '',
        newPassword: '',
        confirmPassword: ''
      }
      this.passwordDialogVisible = true
      // 重置表单验证
      this.$nextTick(() => {
        this.$refs.passwordForm && this.$refs.passwordForm.clearValidate()
      })
    },

    // 提交密码修改
    submitPasswordForm() {
      this.$refs.passwordForm.validate(async (valid) => {
        if (!valid) {
          return false
        }
        try {
          this.submittingPassword = true
          const res = await changePassword({
            id: this.currentUser.id,
            password: this.passwordForm.password,
            newPassword: this.passwordForm.newPassword
          })
          // 检查 success 字段或 code === 1000
          if (res.success === true || res.code === 1000) {
            this.$message.success('密码修改成功')
            this.passwordDialogVisible = false
          } else {
            this.$message.error(res.message || res.msg || '密码修改失败')
          }
        } catch (error) {
          console.error('密码修改失败:', error)
          this.$message.error('密码修改失败，请稍后重试')
        } finally {
          this.submittingPassword = false
        }
      })
    },

    // 获取用户角色名称
    getUserRole(userType) {
      const roleMap = {
        0: '学生',
        1: '教师',
        2: '管理员'
      }
      return roleMap[userType] || '普通用户'
    },

    // 生成日历数据
    generateCalendar(year, month) {
      const calendarDays = []

      // 获取当月第一天是星期几 (0=周日, 1=周一, ..., 6=周六)
      const firstDay = new Date(year, month - 1, 1).getDay()
      // 转换为周一开始 (周日=6, 周一=0, 周二=1, ...)
      const startDay = firstDay === 0 ? 6 : firstDay - 1

      // 获取当月天数
      const daysInMonth = new Date(year, month, 0).getDate()

      // 今天的日期
      const today = new Date()
      const isCurrentMonth = today.getFullYear() === year && today.getMonth() + 1 === month
      const todayDate = today.getDate()

      // 添加空白天（月初前的空格）
      for (let i = 0; i < startDay; i++) {
        calendarDays.push({
          date: null,
          isToday: false,
          isChecked: false,
          detail: null
        })
      }

      // 添加当月的日期
      for (let i = 1; i <= daysInMonth; i++) {
        calendarDays.push({
          date: i,
          isToday: isCurrentMonth && i === todayDate,
          isChecked: false, // 默认未打卡，等后端数据返回后更新
          detail: null
        })
      }

      return calendarDays
    },

    // 更新日历（生成日历并加载打卡数据）
    async updateCalendar() {
      // 更新月份显示
      this.currentMonthDisplay = `${this.currentYear}年 ${this.currentMonth}月`

      // 生成日历布局
      this.calendarDays = this.generateCalendar(this.currentYear, this.currentMonth)

      // 加载打卡数据
      await this.loadCheckinData(this.currentYear, this.currentMonth)
    },

    // 加载打卡数据
    async loadCheckinData(year, month) {
      try {
        this.calendarLoading = true

        // 检查用户信息是否已加载
        if (!this.currentUser || !this.currentUser.id) {
          return
        }

        // 调用统一打卡数据接口（包括视频学习和集中学习）
        const res = await getUnifiedMonthlyCheckin({
          year: year,
          month: month
        })

        // 检查响应状态
        if (res.code === 1000 || res.success === true) {
          const data = res.data
          const checkedDates = data.dates || []
          const details = data.details || {}

          // 更新日历数据，标记已打卡的日期
          this.calendarDays.forEach(day => {
            if (day.date && checkedDates.includes(day.date)) {
              day.isChecked = true
              day.detail = details[day.date] || null
            }
          })
        } else {
          console.error('获取打卡数据失败:', res.message || res.msg)
        }
      } catch (error) {
        console.error('加载打卡数据失败:', error)
        this.$message.error('加载打卡数据失败')
      } finally {
        this.calendarLoading = false
      }
    },

// 计算连续打卡天数
    calculateConsecutiveDays(dates) {
      if (!dates || dates.length === 0) return 0

      // 排序日期
      const sortedDates = [...dates].sort((a, b) => a - b)

      // 从最后一个日期开始往前计算连续天数
      let consecutiveDays = 1
      for (let i = sortedDates.length - 1; i > 0; i--) {
        if (sortedDates[i] - sortedDates[i - 1] === 1) {
          consecutiveDays++
        } else {
          break
        }
      }

      return consecutiveDays
    },

    // 上一月
    previousMonth() {
      if (this.currentMonth === 1) {
        this.currentYear--
        this.currentMonth = 12
      } else {
        this.currentMonth--
      }
      this.updateCalendar()
    },

    // 下一月
    nextMonth() {
      if (this.currentMonth === 12) {
        this.currentYear++
        this.currentMonth = 1
      } else {
        this.currentMonth++
      }
      this.updateCalendar()
    },

    // 点击日期
    async handleDayClick(day) {
      // 只有已打卡的日期才能点击查看详情
      if (day.date && day.isChecked) {
        try {
          // 调用接口获取每日学习详情
          const res = await getDailyStudyDetail({
            year: this.currentYear,
            month: this.currentMonth,
            day: day.date
          })

          // 检查响应状态
          if (res.code === 1000 || res.success === true) {
            this.selectedDateDetail = {
              date: `${this.currentYear}年${this.currentMonth}月${day.date}日`,
              studyDuration: res.data.studyDuration || 0,
              watchedVideos: res.data.watchedVideos || 0,
              completedVideos: res.data.completedVideos || 0
            }
            this.showDetailDialog = true
          } else {
            this.$message.error(res.message || res.msg || '获取学习详情失败')
          }
        } catch (error) {
          console.error('获取每日学习详情失败:', error)
          this.$message.error('获取学习详情失败，请稍后重试')
        }
      }
    }
  }
}
</script>

<style scoped>
.profile-page {
  width: 100%;
}

/* 个人信息卡片 */
.profile-card {
  position: relative;
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  margin-bottom: 32px;
  min-height: 150px;
}

.profile-content {
  display: flex;
  align-items: center;
  gap: 24px;
}

.avatar-wrapper {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  border: 4px solid #f8fafc;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  background: #f1f5f9;
  flex-shrink: 0;
  position: relative;
  cursor: pointer;
  transition: all 0.3s;
}

.avatar-wrapper:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: scale(1.05);
}

.avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s;
  color: white;
}

.avatar-wrapper:hover .avatar-overlay {
  opacity: 1;
}


.upload-text {
  font-size: 12px;
  font-weight: 500;
}

.profile-info {
  flex: 1;
}

/* 手机端不需要 flex 拉伸 */
@media (max-width: 768px) {
  .profile-info {
    flex: none;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.profile-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.user-name {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.user-badge {
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(26, 113, 255, 0.05);
  border: 1px solid rgba(26, 113, 255, 0.1);
  color: #1a71ff;
  font-size: 12px;
  font-weight: 700;
}

.profile-meta {
  display: flex;
  align-items: center;
  gap: 16px;
  color: #64748b;
  font-size: 14px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
}


.edit-btn {
  padding: 8px 20px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  cursor: pointer;
  transition: all 0.2s;
}

.edit-btn:hover {
  background: #f8fafc;
}

.password-btn {
  padding: 8px 20px;
  border-radius: 8px;
  border: 1px solid #1a71ff;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: #1a71ff;
  cursor: pointer;
  transition: all 0.2s;
}

.password-btn:hover {
  background: #1a71ff;
  color: white;
}

/* 学习统计区域 */
.statistics-section {
  position: relative;
  margin-bottom: 32px;
  min-height: 300px;
}

/* Loading 遮罩 */
.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  backdrop-filter: blur(2px);
  border-radius: 16px;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: #1a71ff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  margin: 0;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 0 4px;
}

.section-indicator {
  width: 6px;
  height: 20px;
  background: #1a71ff;
  border-radius: 999px;
}

.section-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.statistics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
}

.stat-card {
  position: relative;
  background: white;
  padding: 24px;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.3s;
}

.stat-card:hover {
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
}

.stat-card.blue:hover {
  box-shadow: 0 12px 24px rgba(59, 130, 246, 0.08);
}

.stat-card.purple:hover {
  box-shadow: 0 12px 24px rgba(168, 85, 247, 0.08);
}

.stat-card.green:hover {
  box-shadow: 0 12px 24px rgba(16, 185, 129, 0.08);
}

.stat-bg {
  position: absolute;
  top: 0;
  right: 0;
  width: 96px;
  height: 96px;
  border-bottom-left-radius: 999px;
  pointer-events: none;
  transition: transform 0.3s;
}

.stat-card.blue .stat-bg {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, transparent 100%);
}

.stat-card.purple .stat-bg {
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.1) 0%, transparent 100%);
}

.stat-card.green .stat-bg {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, transparent 100%);
}

.stat-card:hover .stat-bg {
  transform: scale(1.1);
}

.stat-content {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}


.blue-icon {
  background: rgba(59, 130, 246, 0.05);
  color: #3b82f6;
}

.purple-icon {
  background: rgba(168, 85, 247, 0.05);
  color: #a855f7;
}

.green-icon {
  background: rgba(16, 185, 129, 0.05);
  color: #10b981;
}

.stat-label {
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  margin: 0 0 4px 0;
}

.stat-value-wrapper {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.stat-value {
  font-size: 30px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.025em;
}

.stat-unit {
  font-size: 14px;
  font-weight: 600;
  color: #94a3b8;
}

/* 高亮卡片（连续打卡） */
.stat-card.highlight {
  background: #1a71ff;
  box-shadow: 0 8px 16px rgba(26, 113, 255, 0.3);
  transition: all 0.3s;
}

.stat-card.highlight:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(26, 113, 255, 0.4);
}

.stat-bg-highlight {
  position: absolute;
  top: -32px;
  right: -32px;
  width: 128px;
  height: 128px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  pointer-events: none;
  transform: rotate(12deg);
  transition: transform 0.3s;
}

.stat-card.highlight:hover .stat-bg-highlight {
  transform: rotate(12deg) scale(1.1);
}

.highlight-icon {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  backdrop-filter: blur(4px);
}

.highlight-label {
  color: rgba(255, 255, 255, 0.8);
}

.highlight-value {
  color: white;
}

.highlight-unit {
  color: rgba(255, 255, 255, 0.9);
}


/* 打卡日历 */
.calendar-section {
  background: white;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  padding: 24px;
}

.calendar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
}

.calendar-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.calendar-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  border-radius: 8px;
  padding: 6px;
}

.control-btn {
  padding: 4px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.control-btn:hover {
  background: #e2e8f0;
}


.current-month {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  padding: 0 8px;
}

.calendar-wrapper {
  position: relative;
  width: 100%;
  min-height: 350px;
}

.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 16px;
  text-align: center;
}

.weekday {
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  background: #f1f5f9;
  border: 1px solid #f1f5f9;
  border-radius: 12px;
  overflow: hidden;
}

.calendar-day {
  aspect-ratio: 1;
  background: white;
  padding: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
}

.calendar-day.is-empty {
  background: white;
}

.calendar-day.is-clickable {
  cursor: pointer;
  transition: all 0.2s;
}

.calendar-day.is-clickable:hover {
  background: #f8fafc;
  transform: scale(1.05);
}

.calendar-day.is-today {
  background: rgba(26, 113, 255, 0.05);
}

.calendar-day.is-today::before {
  content: '';
  position: absolute;
  inset: 4px;
  border: 2px solid #1a71ff;
  border-radius: 8px;
  pointer-events: none;
}

.day-number {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
}

.calendar-day.is-today .day-number {
  font-weight: 700;
  color: #1a71ff;
}

.check-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #52c41a;
  margin-top: 4px;
}

.calendar-legend {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.legend-dot.checked {
  background: #52c41a;
}

.legend-dot.today {
  width: 10px;
  height: 10px;
  border-radius: 8px;
  border: 2px solid #1a71ff;
  background: rgba(26, 113, 255, 0.1);
}

.legend-item span {
  font-size: 12px;
  color: #64748b;
}

/* ==================== 手机端适配 (768px 及以下) ==================== */
@media (max-width: 768px) {
  /* 个人信息卡片 - 改为纵向垂直排列 */
  .profile-content {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 16px;
  }

  /* 头像 - 缩小尺寸 */
  .avatar-wrapper {
    width: 72px;
    height: 72px;
    border-width: 3px;
    flex-shrink: 0;
  }

  /* 移除 hover 遮罩，简化交互 */
  .avatar-overlay {
    display: none;
  }

  /* 头像在手机端移除 hover 阴影，简化样式 */
  .avatar-wrapper:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    transform: none;
  }

  /* 用户名和角色 */
  .profile-header {
    flex-direction: column;
    justify-content: center;
    gap: 8px;
    margin-bottom: 0;
  }

  .user-name {
    font-size: 18px;
  }

  /* 部门信息改为纵向排列 */
  .profile-meta {
    flex-direction: column;
    gap: 8px;
    font-size: 13px;
    justify-content: center;
    align-items: center;
  }

  /* 操作按钮 - 堆叠 + 全宽 */
  .action-buttons {
    flex-direction: column;
    width: 100%;
    gap: 10px;
    margin-top: 8px;
  }

  .edit-btn,
  .password-btn {
    padding: 10px 16px;
    font-size: 13px;
    width: 100%;
    height: 40px;
  }

  /* 学习统计区域 - 强制 2 列网格 */
  .statistics-grid {
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  /* 统计卡片 - 缩小 padding 和字体 */
  .stat-card {
    padding: 16px;
    min-height: 160px;
  }

  .stat-content {
    gap: 12px;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }

  .stat-label {
    font-size: 12px;
  }

  .stat-value {
    font-size: 24px;
  }

  .stat-unit {
    font-size: 12px;
  }

  /* 隐藏装饰性背景 */
  .stat-bg {
    display: none;
  }

  .stat-bg-highlight {
    display: none;
  }
}

/* ==================== 超小屏手机 (480px 及以下) ==================== */
@media (max-width: 480px) {
  .profile-card {
    padding: 16px;
    margin-bottom: 24px;
  }

  .avatar-wrapper {
    width: 68px;
    height: 68px;
  }

  .user-name {
    font-size: 16px;
  }

  .edit-btn,
  .password-btn {
    padding: 9px 12px;
    font-size: 12px;
    height: 36px;
  }

  .statistics-grid {
    gap: 12px;
  }

  .stat-card {
    padding: 12px;
    min-height: 140px;
  }

  .stat-value {
    font-size: 20px;
  }

  .section-title {
    font-size: 16px;
  }
}

/* 详情弹窗样式 */
.detail-content {
  padding: 8px 0;
}

.detail-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  margin-bottom: 20px;
}


.detail-date {
  font-size: 16px;
  font-weight: 700;
  color: white;
}

.detail-stats {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 12px;
  transition: all 0.2s;
}

.detail-item:hover {
  background: #f1f5f9;
  transform: translateX(4px);
}

.detail-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}


.detail-icon.blue {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.detail-icon.purple {
  background: rgba(168, 85, 247, 0.1);
  color: #a855f7;
}

.detail-icon.green {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.detail-info {
  flex: 1;
}

.detail-label {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 4px 0;
  font-weight: 500;
}

.detail-value {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}
</style>
